import React from 'react';
import { Check, ChevronDown, Coins } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { cn } from '@cotera/client/v0/lib/utils';

export type ModelMenuItem = {
  id: string;
  label: string;
  creditMultiplier: number;
};

export type ModelMenuGroup = {
  key: string;
  label: string;
  models: ModelMenuItem[];
};

const GROUP_LABEL_CLASS = 'text-xs font-semibold text-muted-foreground px-1';

const ModelMenuGroups: React.FC<{
  groups: ModelMenuGroup[];
  value: string;
  onChange: (id: string) => void;
}> = ({ groups, value, onChange }) =>
  groups
    .filter((g) => g.models.length > 0)
    .map((group) => (
      <DropdownMenuGroup key={group.key}>
        <DropdownMenuLabel className={cn(GROUP_LABEL_CLASS, 'mt-2')}>
          {group.label}
        </DropdownMenuLabel>
        {group.models.map((m) => (
          <DropdownMenuItem key={m.id} onSelect={() => onChange(m.id)}>
            <Check
              className={cn(
                'h-4 w-4',
                m.id === value ? 'visible' : 'invisible'
              )}
            />
            <span className="flex items-center justify-between flex-1">
              <span>{m.label}</span>
              <span className="flex items-center gap-1 text-muted-foreground text-xs ml-4">
                <Coins className="h-3 w-3 text-emerald-500" />
                {m.creditMultiplier}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
    ));

/**
 * Model dropdown shared by the agent pickers: the current lineup at the top
 * level, and superseded versions tucked into an "Other models" submenu.
 */
export const ModelPickerMenu: React.FC<{
  value: string;
  valueLabel: string;
  groups: ModelMenuGroup[];
  otherGroups: ModelMenuGroup[];
  onChange: (id: string) => void;
}> = ({ value, valueLabel, groups, otherGroups, onChange }) => {
  const hasOther = otherGroups.some((g) => g.models.length > 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex w-auto items-center gap-2 rounded-md px-3 py-2 h-9 text-sm whitespace-nowrap outline-none',
          'border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors',
          'hover:bg-accent hover:text-foreground aria-expanded:bg-accent aria-expanded:text-foreground',
          'focus-visible:ring-[3px] focus-visible:ring-ring/50'
        )}
      >
        <span className="line-clamp-1">{valueLabel}</span>
        <ChevronDown className="size-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <ModelMenuGroups groups={groups} value={value} onChange={onChange} />
        {hasOther && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Other models</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto">
                <ModelMenuGroups
                  groups={otherGroups}
                  value={value}
                  onChange={onChange}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
